import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { path_url } from "../../Config/config";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import OutsideClickHandler from "react-outside-click-handler";
import { toast } from "react-toastify";
import { filterByStatus } from "../../Utils/StatusConfig";
import {
  FaColumns,
  FaCopy,
  FaFileCsv,
  FaFilePdf,
  FaPrint,
  FaSearch,
  FaUsers,
  FaPlus,
  FaMicrophone,
  FaTimes,
} from "react-icons/fa";
import "../../Style/DataTable.css";

/* ─── Status Badge Helper ─────────────────────────────────── */
const getStatusClass = (status = "") => {
  const s = status.toUpperCase().trim();
  if (s === "OPEN") return "status-open";
  if (s === "QUALIFIED") return "status-qualified";
  if (s.includes("CONVERT")) return "status-converted";
  if (s.includes("DISQUAL")) return "status-disqualified";
  if (s.includes("NEW") || s.includes("OPP")) return "status-new-opportunity";
  if (s.includes("PROGRESS") || s.includes("IN ")) return "status-in-progress";
  return "status-default";
};

const StatusBadge = ({ value }) =>
  value ? (
    <span className={`status-badge ${getStatusClass(value)}`}>{value}</span>
  ) : (
    <span className="status-badge status-default">—</span>
  );

/* ─── Avatar ─────────────────────────────────────────────── */
const getInitials = (first = "", last = "") =>
  `${first[0] || ""}${last[0] || ""}`.toUpperCase() || "?";

/* ──────────────────────────────────────────────────────────── */
function CustomerDashboardTable({ filterStatus }) {
  const [customerList, setCustomerList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedColumns, setSelectedColumns] = useState({
    Name: true,
    Company: true,
    Status: true,
    Mobile: true,
    Email: true,
    Notes: true,
    Pincode: true,
    Product: true,
    Address1: true,
    Address2: true,
    City: true,
    State: true,
    Country: true,
    "Created Date": true,
  });
  const [colDropOpen, setColDropOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState(""); // raw live speech display
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("accessToken");
  const adminId = localStorage.getItem("adminId");

  const getDisplayTitle = () => {
    if (!filterStatus) return "All Customers";
    if (filterStatus === "total") return "Total Customers";
    if (filterStatus === "converted") return "Converted Customers";
    if (filterStatus === "active") return "Active Customers";
    if (filterStatus === "inactive") return "Inactive Customers";
    return `${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Customers`;
  };
  const displayTitle = getDisplayTitle();

  /* ── Fetch ── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { year, month } = location.state || {};
        const res = await axios.get(`${path_url}/customer/dashboard/${adminId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: { year, month },
        });
        setCustomerList(res.data.body.customerList || []);
        setFilteredData(res.data.body.customerList || []);
      } catch {
        setCustomerList([]);
        setFilteredData([]);
        toast.error("Something went wrong");
      }
    };
    fetchData();
  }, [adminId, token, location.state]);

  /* ── Stop words for intelligent keyword extraction ── */
  const STOP_WORDS = new Set([
    "i", "am", "im", "searching", "for", "can", "you", "please", "show", "me",
    "find", "all", "open", "details", "search", "company", "customer", "customers",
    "the", "a", "an", "of", "and", "in", "to", "my", "is", "where", "what",
    "looking", "want", "see", "get", "fetch", "display", "list", "give",
    "whose", "with", "has", "have", "about", "tell", "us", "on", "by",
    "at", "from", "that", "this", "those", "these", "it", "its",
    "up", "who", "how", "just", "do", "did", "will", "would", "could",
  ]);

  const extractKeywords = (text) => {
    const cleanText = text
      .toLowerCase()
      .replace(/[.,!?:;'"]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    const words = cleanText.split(" ");
    const keywords = words.filter((word) => word.length > 0 && !STOP_WORDS.has(word));
    return keywords.join(" ");
  };

  /* ── Filter / Search ── */
  useEffect(() => {
    let base = customerList;
    if (filterStatus) base = filterByStatus(customerList, filterStatus, "customer");

    if (!searchText) {
      setFilteredData(base);
      return;
    }

    const cleanedText = extractKeywords(searchText);
    const keywords = cleanedText
      .split(" ")
      .filter((k) => k.trim() !== "");

    if (keywords.length === 0) {
      setFilteredData(base);
      return;
    }

    setFilteredData(
      base.filter((item) => {
        const itemValues = Object.values(item).map((v) =>
          String(v).toLowerCase()
        );
        return keywords.every((kw) =>
          itemValues.some((val) => val.includes(kw))
        );
      })
    );
  }, [searchText, customerList, filterStatus]);

  /* ── Clear search helper ── */
  const clearSearch = () => {
    if (window.currentVoiceRecognition) {
      window.currentVoiceRecognition.stop();
      window.currentVoiceRecognition = null;
    }
    setIsListening(false);
    setLiveTranscript("");
    setSearchText("");
  };

  /* ── Voice clear command keywords ── */
  const CLEAR_COMMANDS = [
    "clear", "clear text", "clear input", "remove text", "erase",
    "delete text", "empty", "clear all", "clear search", "reset search",
    "remove search", "cancel search", "delete", "reset",
  ];

  const isClearCommand = (transcript) => {
    const cleaned = transcript
      .toLowerCase()
      .replace(/[.,!?:;'"]/g, "")
      .trim();
    return CLEAR_COMMANDS.some((cmd) => cleaned.includes(cmd));
  };

  const handleVoiceSearch = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast.error("Voice search is not supported in this browser. Please use Chrome.");
      return;
    }

    if (window.currentVoiceRecognition) {
      window.currentVoiceRecognition.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    window.currentVoiceRecognition = recognition;

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setLiveTranscript("");
      setSearchText("");
    };

    recognition.onresult = (event) => {
      let fullTranscript = "";
      for (let i = 0; i < event.results.length; i++) {
        fullTranscript += event.results[i][0].transcript;
      }

      if (isClearCommand(fullTranscript)) {
        clearSearch();
        toast.info("Search cleared.", { toastId: "voiceClear", autoClose: 1500 });
        if (window.currentVoiceRecognition) {
          window.currentVoiceRecognition.stop();
        }
        return;
      }

      setLiveTranscript(fullTranscript);

      const keywords = extractKeywords(fullTranscript);
      setSearchText(keywords);
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed") {
        toast.error("Microphone permission denied. Please allow microphone access.");
      } else if (event.error === "no-speech") {
        toast.warning("No speech detected. Please try again.");
      } else if (event.error !== "aborted") {
        toast.error(`Voice search error: ${event.error}`);
      }
      setIsListening(false);
      setLiveTranscript("");
      window.currentVoiceRecognition = null;
    };

    recognition.onend = () => {
      setIsListening(false);
      setLiveTranscript("");
      window.currentVoiceRecognition = null;
    };

    recognition.start();
  };

  const caseSort = (sel) => (a, b) => {
    const av = (sel(a) || "").toString().toLowerCase();
    const bv = (sel(b) || "").toString().toLowerCase();
    return av > bv ? 1 : av < bv ? -1 : 0;
  };

  const numSort = (sel) => (a, b) => {
    const av = parseFloat(sel(a)) || 0;
    const bv = parseFloat(sel(b)) || 0;
    return av - bv;
  };

  const dateSort = (sel) => (a, b) => {
    const av = sel(a) ? new Date(sel(a)).getTime() : 0;
    const bv = sel(b) ? new Date(sel(b)).getTime() : 0;
    return av - bv;
  };

  /* ── Column Definitions ── */
  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.firstname} ${row.lastname}`,
      sortable: true,
      sortFunction: caseSort((row) => `${row.firstname} ${row.lastname}`),
      width: "180px",
      cell: (row) => (
        <div
          className="lead-name-cell"
          onClick={() => navigate(`/customer-details/${row.id}`)}
          title={`${row.firstname} ${row.lastname}`}
        >
          <div className="lead-avatar">
            {getInitials(row.firstname, row.lastname)}
          </div>
          <span className="lead-name-text">
            {row.firstname} {row.lastname}
          </span>
        </div>
      ),
    },
    {
      name: "Company",
      selector: (row) => row.companyname || "",
      sortable: true,
      sortFunction: caseSort((row) => row.companyname || ""),
      width: "140px",
      cell: (row) => (
        <span style={{ color: "var(--clr-text)", fontWeight: 500 }}>
          {row.companyname || "—"}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status || "",
      sortable: true,
      sortFunction: caseSort((row) => row.status || ""),
      width: "160px",
      cell: (row) => <StatusBadge value={row.status} />,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile || "",
      sortable: true,
      sortFunction: numSort((row) => row.mobile || ""),
      width: "150px",
      cell: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px", color: "var(--clr-text-muted)" }}>
          {row.mobile || "—"}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email || "",
      sortable: true,
      sortFunction: caseSort((row) => row.email || ""),
      width: "200px",
      cell: (row) => (
        <span style={{ color: "#2563eb", fontSize: "12px" }}>
          {row.email || "—"}
        </span>
      ),
    },
    {
      name: "Notes",
      selector: (row) => row.notes || "",
      sortable: true,
      sortFunction: caseSort((row) => row.notes || ""),
      width: "150px",
      cell: (row) => (
        <span
          title={row.notes}
          style={{
            maxWidth: "130px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            color: "var(--clr-text-muted)",
            fontSize: "12px",
          }}
        >
          {row.notes || "—"}
        </span>
      ),
    },
    {
      name: "Pincode",
      selector: (row) => row.pincode || "",
      sortable: true,
      sortFunction: numSort((row) => row.pincode || ""),
      width: "100px",
      cell: (row) => (
        <span style={{ color: "var(--clr-text-muted)", fontSize: "12px" }}>
          {row.pincode || "—"}
        </span>
      ),
    },
    {
      name: "Product",
      selector: (row) => row.product || "",
      sortable: true,
      sortFunction: caseSort((row) => row.product || ""),
      width: "120px",
    },
    {
      name: "Address1",
      selector: (row) => row.add1 || "",
      sortable: true,
      sortFunction: caseSort((row) => row.add1 || ""),
      width: "150px",
      cell: (row) => (
        <span style={{ color: "var(--clr-text-muted)", fontSize: "12px" }}>
          {row.add1 || "—"}
        </span>
      ),
    },
    {
      name: "Address2",
      selector: (row) => row.add2 || "",
      sortable: true,
      sortFunction: caseSort((row) => row.add2 || ""),
      width: "150px",
      cell: (row) => (
        <span style={{ color: "var(--clr-text-muted)", fontSize: "12px" }}>
          {row.add2 || "—"}
        </span>
      ),
    },
    {
      name: "City",
      selector: (row) => row.city || "",
      sortable: true,
      sortFunction: caseSort((row) => row.city || ""),
      width: "110px",
    },
    {
      name: "State",
      selector: (row) => row.state || "",
      sortable: true,
      sortFunction: caseSort((row) => row.state || ""),
      width: "110px",
    },
    {
      name: "Country",
      selector: (row) => row.country || "",
      sortable: true,
      sortFunction: caseSort((row) => row.country || ""),
      width: "110px",
    },
  ];

  const visibleColumns = columns.filter((col) => selectedColumns[col.name]);

  /* ── Export utilities ── */
  const printTable = () => {
    const win = window.open("", "", "width=900,height=700");
    const html = `
      <html><head><title>Customer Table</title>
      <style>
        body { font-family: sans-serif; padding: 20px; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #4f46e5; color: #fff; padding: 8px 12px; text-align: left; font-size: 12px; }
        td { padding: 7px 12px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
        tr:nth-child(even) td { background: #f8fafc; }
      </style></head><body>
      <h2 style="color:#4f46e5;margin-bottom:16px">Customer Report</h2>
      <table>
        <thead><tr>${visibleColumns.map((c) => `<th>${c.name}</th>`).join("")}</tr></thead>
        <tbody>${filteredData.map((row) => `<tr>${visibleColumns.map((c) => `<td>${c.selector(row) || "—"}</td>`).join("")}</tr>`).join("")}</tbody>
      </table></body></html>`;
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const copyToClipboard = () => {
    const text = filteredData
      .map((row) => visibleColumns.map((col) => col.selector(row)).join("\t"))
      .join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Table data copied to clipboard!");
  };

  const exportCSV = () => {
    const csv =
      "data:text/csv;charset=utf-8," +
      [
        columns.map((c) => `"${c.name}"`).join(","),
        ...filteredData.map((row) =>
          columns.map((c) => `"${(c.selector(row) || "").toString().replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", "customers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text("Customer Report", 14, 14);
      doc.autoTable({
        startY: 22,
        head: [columns.map((c) => c.name)],
        body: filteredData.map((row) => columns.map((c) => c.selector(row) || "")),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      doc.save("customers.pdf");
    } catch {
      toast.error("PDF export requires jspdf-autotable plugin.");
    }
  };

  /* ── Custom Styles for DataTable ── */
  const customStyles = {
    table: {
      style: { fontFamily: "'Inter', sans-serif" },
    },
    headRow: {
      style: {
        background: "#f1f5f9",
        borderBottom: "2px solid #e2e8f0",
        minHeight: "46px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: "700",
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        paddingLeft: "14px",
        paddingRight: "14px",
      },
    },
    rows: {
      style: {
        minHeight: "54px",
        borderBottomColor: "#e2e8f0",
        fontFamily: "'Inter', sans-serif",
        transition: "background 0.15s",
      },
      highlightOnHoverStyle: {
        background: "#f8f9ff",
        boxShadow: "inset 3px 0 0 #4f46e5",
        transition: "background 0.15s, box-shadow 0.15s",
      },
      stripedStyle: {
        background: "#fafbfe",
      },
    },
    cells: {
      style: {
        paddingLeft: "14px",
        paddingRight: "14px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e2e8f0",
        background: "#fafbff",
        fontSize: "13px",
        color: "#64748b",
        fontFamily: "'Inter', sans-serif",
        minHeight: "52px",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
      pageButtonsStyle: {
        borderRadius: "6px",
        height: "32px",
        width: "32px",
        padding: "0",
        margin: "2px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        color: "#64748b",
        fill: "#64748b",
        backgroundColor: "transparent",
        "&:disabled": { opacity: 0.4 },
        "&:hover:not(:disabled)": {
          backgroundColor: "#ede9fe",
          fill: "#4f46e5",
        },
        "&:focus": { outline: "none" },
      },
    },
    noData: {
      style: {
        padding: "40px 20px",
        color: "#94a3b8",
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
      },
    },
  };

  /* ──────────────────────────────────────────────────────────── */
  return (
    <div className="lead-table-card">
      {/* ── Card Header ── */}
      <div className="lead-card-header">
        <div className="lead-card-title">
          <div className="lead-card-icon">
            <FaUsers />
          </div>
          <h4>{displayTitle}</h4>
          {filteredData.length > 0 && (
            <span className="lead-card-badge">{filteredData.length} records</span>
          )}
        </div>
        {!filterStatus && (
          <button
            type="button"
            onClick={() => navigate("/new-customer")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#0d9488",
              color: "#ffffff",
              border: "1px solid #0d9488",
              borderRadius: "6px",
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: "600",
              fontFamily: "var(--font-base)",
              cursor: "pointer",
              height: "34px",
              transition: "all 0.15s ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0f766e";
              e.currentTarget.style.borderColor = "#0f766e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#0d9488";
              e.currentTarget.style.borderColor = "#0d9488";
            }}
          >
            <FaPlus style={{ fontSize: "11px" }} />
            Create Customer
          </button>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className="gai-toolbar">
        {/* Action Buttons */}
        <div className="gai-toolbar-actions">
          <button type="button" className="gai-tb-btn" onClick={copyToClipboard} title="Copy to clipboard">
            <FaCopy style={{ fontSize: "12px" }} /> Copy
          </button>
          <button type="button" className="gai-tb-btn" onClick={exportCSV} title="Export CSV">
            <FaFileCsv style={{ fontSize: "12px" }} /> CSV
          </button>
          <button type="button" className="gai-tb-btn" onClick={exportPDF} title="Export PDF">
            <FaFilePdf style={{ fontSize: "12px" }} /> PDF
          </button>
          <button type="button" className="gai-tb-btn" onClick={printTable} title="Print">
            <FaPrint style={{ fontSize: "12px" }} /> Print
          </button>

          {/* Column Visibility */}
          <OutsideClickHandler onOutsideClick={() => setColDropOpen(false)}>
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="gai-tb-btn"
                onClick={() => setColDropOpen((p) => !p)}
                title="Toggle columns"
              >
                <FaColumns style={{ fontSize: "12px" }} /> Columns
              </button>
              {colDropOpen && (
                <div
                  className="col-vis-dropdown"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    zIndex: 1050,
                  }}
                >
                  {Object.keys(selectedColumns).map((colName) => {
                    const isVis = !!selectedColumns[colName];
                    return (
                      <div
                        key={colName}
                        className="col-vis-item"
                        onClick={() =>
                          setSelectedColumns((prev) => ({
                            ...prev,
                            [colName]: !prev[colName],
                          }))
                        }
                        style={{ background: isVis ? "#f3f4f6" : "transparent" }}
                      >
                        <input
                          type="checkbox"
                          checked={isVis}
                          onChange={() => { }}
                          style={{ cursor: "pointer" }}
                        />
                        <span>{colName}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </OutsideClickHandler>
        </div>

        {/* Search */}
        <div className="gai-search-wrapper">
          <FaSearch className="gai-search-icon" />
          <input
            id="customer-search"
            type="text"
            className="gai-search-input"
            value={isListening ? liveTranscript : searchText}
            onChange={(e) => {
              if (!isListening) setSearchText(e.target.value);
            }}
            placeholder={isListening ? "Speak now…" : "Search customers…"}
            autoComplete="off"
            style={{
              paddingRight: (isListening ? liveTranscript : searchText) ? "52px" : "30px",
              fontStyle: isListening ? "italic" : "normal",
              color: isListening ? "#0d9488" : "inherit",
            }}
            readOnly={isListening}
          />
          {/* Clear ✕ button — visible when input has text */}
          {(isListening ? liveTranscript : searchText) && (
            <button
              type="button"
              onClick={clearSearch}
              title="Clear search"
              style={{
                position: "absolute",
                right: "28px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
                color: "#94a3b8",
                fontSize: "12px",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
            >
              <FaTimes />
            </button>
          )}
          <FaMicrophone
            className={`gai-voice-icon ${isListening ? "listening" : ""}`}
            onClick={handleVoiceSearch}
            title={isListening ? "Click to stop listening" : "Search by voice"}
          />
        </div>
      </div>

      {/* ── Data Table ── */}
      <DataTable
        columns={visibleColumns}
        data={filteredData}
        pagination
        highlightOnHover
        striped
        responsive
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 280px)"
        sortIcon={
          <span style={{ marginLeft: "4px", fontSize: "10px", color: "var(--clr-primary)" }}>▲</span>
        }
        customStyles={customStyles}
        noDataComponent={
          <div
            style={{
              padding: "48px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FaUsers style={{ fontSize: "40px", color: "var(--clr-border-md)" }} />
            <p style={{ margin: 0, color: "var(--clr-text-light)", fontSize: "14px", fontWeight: 500 }}>
              {searchText ? `No matching customers found for '${searchText}'` : "No customers found"}
            </p>
            <p style={{ margin: 0, color: "var(--clr-text-light)", fontSize: "12px" }}>
              {searchText ? "Try adjusting your search or speaking a different keyword" : "Try adjusting your search or filter criteria"}
            </p>
          </div>
        }
      />
    </div>
  );
}

export default CustomerDashboardTable;
